/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FabMessageService} from "@fabric/components/message";
import {DISMISS_MSG} from "../../../../common/constants";
import {getDefaultYearRange, isEmpty, range} from "../../../../core/helpers/generic-helpers";
import {VehicleScenario} from "../../../../models/vehicle-scenario-dto.model";
import {PropulsionScenarioService} from "../../../../services/propulsion-scenario-service";

@Component({
    selector: 'app-bulk-prop-scen-update-pop-up',
    templateUrl: './bulk-prop-scen-update-pop-up.component.html',
    styleUrl: './bulk-prop-scen-update-pop-up.component.scss',
    standalone: false
})
export class BulkPropScenUpdatePopUpComponent implements OnInit {
  isRemoveUnmatched = false;
  showSpinner: boolean;

  scenarioYearRange: number[] = []
  selectedStartYear: number;
  selectedEndYear: number;

  scenarios: VehicleScenario[] = [];
  selectedOptions: VehicleScenario[] = [];

  constructor(public snackBar: FabMessageService,
              @Inject(MAT_DIALOG_DATA) protected readonly data: any,
              public dialogRef: MatDialogRef<BulkPropScenUpdatePopUpComponent>,
              readonly propulsionScenarioService: PropulsionScenarioService) {
    if (!isEmpty(data)) {
      this.scenarios = data.scenarios;
    }
  }

  ngOnInit(): void {
    this.getYearRange();
  }

  /**
   * Event handler for closing the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Event handler for confirm button
   */
  onConfirm(): void {
    const data = {
      selectedOptions: this.selectedOptions,
      selectedStartYear: this.selectedStartYear,
      selectedEndYear: this.selectedEndYear,
      isRemoveUnmatched: this.isRemoveUnmatched
    }
    this.dialogRef.close(data);
  }

  /**
   * Event handler for checkbox change event
   */
  onCheckboxChange(): void {
    if (this.isRemoveUnmatched) {
      this.getYearRange();
    }
  }

  /**
   * selects everything in the list
   */
  onSelectAll() {
    this.selectedOptions = this.scenarios;
  }

  /**
   * Gets the year range of the selected propulsion scenarios.
   * When scenarios have no penetrations, MIN/MAX year are null — fall back to the default year range
   * so the portfolio update can still proceed with valid start/end years.
   */
  getYearRange(): void {
    this.showSpinner = true;
    this.propulsionScenarioService
    .getYearRangeByIds(this.scenarios.map(scenario => scenario.propulsionScenario.id)).subscribe(
      {
        next: (data: any[]) => {
          const scenarioMinYear = data?.[0]?.MIN_YEAR;
          const scenarioMaxYear = data?.[0]?.MAX_YEAR;
          if (scenarioMinYear == null || scenarioMaxYear == null) {
            const {yearRange, startYear, endYear} = getDefaultYearRange();
            this.selectedStartYear = startYear;
            this.selectedEndYear = endYear;
            this.scenarioYearRange = yearRange;
          } else {
            this.selectedStartYear = scenarioMinYear;
            this.selectedEndYear = scenarioMaxYear;
            this.scenarioYearRange = range(this.selectedStartYear, this.selectedEndYear + 1);
          }
        },
        error: (err) => {
          this.showSpinner = false;
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.showSpinner = false;
        }
      }
    );
  }
}
